import { useMemo, useRef, useState } from 'react'

import * as THREE from 'three'
import { useFrame, useLoader, Canvas } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'

export default function AllLogos(props) {
    return (
      <Canvas camera={{ position: [0, 0, 5], fov: 30 }}>
        <color attach="background" args={['#000000']} />

        <OrbitControls
          enableRotate={false}
          enablePan={false}
          enableZoom
          minDistance={25}
          maxDistance={70}
        />

        <LogoCloudHelix logos={props.logos} />
      </Canvas>
    )
}

function LogoCloudHelix({ logos = [] }) {
  const group = useRef()
  const textures = useLoader(THREE.TextureLoader, logos)
  const [activeIndex, setActiveIndex] = useState(null)
  const [version, setVersion] = useState(0);

  const RADIUS = 6
  const HEIGHT = 1
  const TURNS = 2
  const BASE_SIZE = 3
  const WINDOW_SIZE = Math.min(4, logos.length)
  const ROTATION_SPEED = 0.005; // radians per frame
  const LOGOS_PER_TURN = WINDOW_SIZE;
  const STEP_ANGLE = (Math.PI * 2) / LOGOS_PER_TURN;

  const rotationAcc = useRef(0);
  const recycleIndex = useRef(WINDOW_SIZE - 1);
  const nextLogoIndex = useRef(WINDOW_SIZE);

  const items = useMemo(() => {
    return textures.map((texture, i) => {
      const { width, height } = texture.image
      const aspect = width / height

      return {
        texture,
        aspect,
        position: helixPosition(
          i,
          textures.length,
          RADIUS,
          HEIGHT,
          TURNS
        )
      }
    })
  }, [textures])

  const slots = useRef(
    Array.from({ length: WINDOW_SIZE }, (_, i) => ({
      logicalIndex: i,
      logoIndex: i
    }))
  );

  function helixPosition(index, total, radius, height, turns = 2) {
    const t = index / total
    const angle = t * Math.PI * 2 * turns
    const y = height * (0.5 - t)

    return [
      Math.cos(angle) * radius,
      y,
      Math.sin(angle) * radius
    ]
  }

  function getHelixSlotPosition(slotIndex) {
    const pitch = 0.7;
    const angle = slotIndex * STEP_ANGLE;

    return [
      Math.cos(angle) * RADIUS,
      -slotIndex * pitch,
      Math.sin(angle) * RADIUS
    ];
  }

  useFrame(() => {
    if (!group.current) return;

    // Continuous rotation
    group.current.rotation.y += ROTATION_SPEED;
    rotationAcc.current += ROTATION_SPEED;

    // One slot per angular step
    if (rotationAcc.current >= STEP_ANGLE) {
      rotationAcc.current -= STEP_ANGLE;

      // Replace the slot that just exited
      const slot = slots.current[recycleIndex.current];
      slot.logoIndex = nextLogoIndex.current % items.length;

      nextLogoIndex.current += 1;

      // advance recycle pointer (circular)
      recycleIndex.current =
        (recycleIndex.current + 1 + WINDOW_SIZE) % WINDOW_SIZE;

      // force material update
      setVersion(v => v + 1);
    }

    // Smooth positional interpolation
    group.current.children.forEach((sprite, i) => {
      const [x, y, z] = getHelixSlotPosition(i);

      sprite.position.x += (x - sprite.position.x) * 0.1;
      sprite.position.y += (y - sprite.position.y) * 0.1;
      sprite.position.z += (z - sprite.position.z) * 0.1;
    });
  });

  return (<>
    {logos && logos.length > 0 ? (
    <group ref={group} rotation={[0.5, 0, 0.5]}>
      {slots.current.map((slot, i) => {
        const item = items[slot.logoIndex];

        return (
          <sprite
            key={slot.logicalIndex} // NEVER changes
            scale={[
              BASE_SIZE * item.aspect,
              BASE_SIZE,
              1
            ]}
            onClick={() =>
              setActiveIndex((activeIndex === i) ? null : i)
            }
          >
            <spriteMaterial
              map={item.texture}
              transparent
              opacity={ ((activeIndex === null) || (activeIndex === i)) ? 1 : 0.75 }
            />
          </sprite>
        )
      })}
    </group>
    ) : ''}
  </>)
}
